import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabaseClient.js";
import { useNavigate } from "react-router-dom";
import BackButton from "../common/BackButton.jsx";
import "./ClubApprovalCard.css";

// 관리자용 동아리 승인 요청 카드 컴포넌트
export default function ClubApprovalCard() {
    // 승인 대기 중인 동아리 목록
    const [apply, setApply] = useState([]);

    // 선택된 동아리 신청 정보
    const [selectedApply, setSelectedApply] = useState(null);

    // 이미지 모달 표시 여부
    const [showImageModal, setShowImageModal] = useState(false);

    const navigate = useNavigate();

    // 관리자 권한 확인 후 승인 요청 목록 조회
    useEffect(() => {
        let interval;

        async function checkAdmin() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) { navigate('/'); return; }

            // 현재 사용자의 관리자 권한 확인
            const { data } = await supabase
                .from('users')
                .select('role')
                .eq('email', user.email)
                .maybeSingle();

            if (!data || data.role !== 'admin') {
                alert('접근 권한이 없습니다.');
                navigate('/');
                return;
            }

            getApplications();

            // 승인 요청 목록을 5초마다 갱신
            interval = setInterval(getApplications, 5000);
        }

        checkAdmin();

        // 컴포넌트 종료 시 반복 실행 해제
        return () => clearInterval(interval);
    }, []);

    // 승인 대기 상태의 동아리 목록 조회
    async function getApplications() {
        const { data, error } = await supabase
            .from("clubs")
            .select("*")
            .eq("approval_status", "대기");

        if (error) { console.log(error); return; }

        setApply(data);
    }

    // 동아리 개설 승인 처리
    async function approveApplication(club) {
        if (!club.created_by) {
            alert("동아리 생성자 정보가 없습니다.");
            return;
        }

        // 동아리 승인 상태 변경 및 동아리장 지정
        const { error: clubError } = await supabase
            .from("clubs")
            .update({
                approval_status: "승인",
                leader_id: club.created_by,
                member_count: (club.member_count || 0) + 1,
            })
            .eq("id", club.id);

        if (clubError) {
            console.log(clubError);
            alert("승인 처리 실패");
            return;
        }

        // 동아리 생성자를 동아리장으로 등록
        const { error: memberError } = await supabase
            .from("club_members")
            .insert({
                club_id: club.id,
                user_id: club.created_by,
                role: "leader",
            });

        if (memberError) {
            console.log(memberError);
            alert("동아리 멤버 추가 실패");
            return;
        }

        alert("승인 처리되었습니다.");
        setSelectedApply(null);
        setShowImageModal(false);
        getApplications();
    }

    // 동아리 개설 거절 처리
    async function rejectApplication(id) {
        const { error } = await supabase
            .from("clubs")
            .delete()
            .eq("id", id);

        if (error) {
            console.log(error);
            alert("거절 처리 실패");
            return;
        }

        alert("거절 처리되었습니다.");
        setSelectedApply(null);
        setShowImageModal(false);
        getApplications();
    }

    // 이미지 보기 모달 열기
    function openImageModal() {
        if (!selectedApply?.image_url) {
            alert("업로드된 이미지가 없습니다.");
            return;
        }

        setShowImageModal(prev => !prev);
    }

    return (
        <>
            <div className="approvalContainer">
                <div className="approvalBack">
                    <BackButton />
                </div>

                {/* 승인 대기 동아리 카드 목록 */}
                <div className="ClubApprovalCard">
                    {apply.map((club) => (
                        <div
                            key={club.id}
                            className="Approval-item"
                            onClick={() => {
                                setSelectedApply(club);
                                setShowImageModal(false);
                            }}
                        >
                            <div className="ClubApprovalCard-left">
                                <h1 className="ClubApprovalCard-name">
                                    {club.name}
                                </h1>

                                {/* 동아리 이미지 미리보기 */}
                                <div
                                    className="ClubApprovalCard-image"
                                    style={{
                                        backgroundImage: club.image_url
                                            ? `url(${club.image_url})`
                                            : "none",
                                        backgroundColor: club.image_url
                                            ? "transparent"
                                            : "darkblue",
                                    }}
                                >
                                    {!club.image_url && "이미지"}
                                </div>
                            </div>

                            <div className="ClubApprovalCard-right">
                                <p className="ClubApprovalCard-description">
                                    {club.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 승인 요청 상세 모달 */}
            {selectedApply && (
                <div className="approvalModal-overlay">
                    <div className={`approvalModal ${showImageModal ? 'approvalModal-expanded' : ''}`}>
                        <button
                            className="approvalModal-close"
                            onClick={() => {
                                setSelectedApply(null);
                                setShowImageModal(false);
                            }}
                        >
                            X
                        </button>

                        <h2>동아리명: {selectedApply.name}</h2>

                        <div className="approvalModal-line" />

                        {/* 동아리 신청 상세 정보 */}
                        <p>간단 설명: {selectedApply.description}</p>
                        <p>회비: {selectedApply.fee}</p>
                        <p>지원 자격: {selectedApply.eligibility}</p>
                        <p>주요 활동: {selectedApply.activities}</p>
                        <p>카테고리: {selectedApply.category}</p>

                        <p>
                            동아리 이미지 등록:{" "}
                            <button onClick={openImageModal}>이미지 보기</button>
                        </p>

                        {/* 등록된 동아리 이미지 */}
                        {showImageModal && (
                            <img
                                src={selectedApply.image_url}
                                alt="동아리 이미지"
                                style={{ width: '100%', marginTop: '10px', borderRadius: '8px' }}
                            />
                        )}

                        {/* 승인 및 거절 버튼 */}
                        <div className="approvalModal-buttons">
                            <button onClick={() => rejectApplication(selectedApply.id)}>
                                거절
                            </button>
                            <button onClick={() => approveApplication(selectedApply)}>
                                승인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}